import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/Base/Breadcrumb";
import { Fragment } from "react";
import { useLocation, Link, useParams } from "react-router-dom";

// Define your route structure
const routeConfig: Record<
  string,
  { label: string | ((params: any) => string); parent?: string }
> = {
  "/": { label: "Dashboard" },
  "/:shopId": { label: "Shop Dashboard" },
  "/:shopId/customer-updates": {
    label: "Customer Updates",
    parent: "/:shopId",
  },
  "/:shopId/customer-updates/:id": {
    label: (params) => `Customer Updates ${params.id}`,
    parent: "/:shopId/customer-updates",
  },
};

export type BreadcrumbsProps = {
  breadcrumbs?: { label: string; href?: string }[];
};

export const Breadcrumbs = ({ breadcrumbs }: BreadcrumbsProps) => {
  const location = useLocation();
  const params = useParams();

  // If breadcrumbs prop is provided, use it directly
  if (breadcrumbs) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbs.map((crumb, index) => (
            <Fragment key={index}>
              <BreadcrumbItem>
                {crumb.href ? (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.href}>{crumb.label}</Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
              {index !== breadcrumbs.length - 1 && <BreadcrumbSeparator />}
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  // Find matching route pattern
  const currentPath = location.pathname;
  const matchedRoute = Object.keys(routeConfig).find((route) => {
    if (route === currentPath) return true;
    // Handle dynamic segments
    const routeRegex = route.replace(/:[\w]+/g, "[^/]+");
    return new RegExp(`^${routeRegex}$`).test(currentPath);
  });

  if (!matchedRoute) {
    return null;
  }

  // Build breadcrumb trail
  const buildBreadcrumbs = (
    route: string
  ): { label: string; href?: string }[] => {
    const config = routeConfig[route];
    if (!config) return [];

    const label =
      typeof config.label === "function" ? config.label(params) : config.label;

    const breadcrumb = {
      label,
      href:
        route === matchedRoute
          ? undefined
          : route.replace(/:[\w]+/g, (match) => {
              const paramName = match.slice(1);
              return params[paramName] || match;
            }),
    };

    if (config.parent) {
      return [...buildBreadcrumbs(config.parent), breadcrumb];
    }

    return [breadcrumb];
  };

  const autoBreadcrumbs = buildBreadcrumbs(matchedRoute);

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {autoBreadcrumbs.map((crumb, index) => (
          <Fragment key={index}>
            <BreadcrumbItem>
              {crumb.href ? (
                <BreadcrumbLink asChild>
                  <Link to={crumb.href}>{crumb.label}</Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
            {index !== autoBreadcrumbs.length - 1 && <BreadcrumbSeparator />}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
